import { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Popconfirm,
  message,
  Image,
  Card,
  Upload,
  Input,
  Modal,
  Form,
  Select,
  Row,
  Col,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
  SearchOutlined,
  DollarOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import api from "../../api";

const { Option } = Select;

const BrandIndex = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState({});
  const [countries, setCountries] = useState([]);
  const [fetchingCountries, setFetchingCountries] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [form] = Form.useForm();
  
  // Use searchParams for URL parameters
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get initial filter values from URL
  const initialSearchText = searchParams.get("search") || "";
  const initialType = searchParams.get("type") || "";
  const initialCountry = searchParams.get("country") || "";
  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const initialPageSize = parseInt(searchParams.get("per_page") || "25", 10);

  // Set state from URL params
  const [searchText, setSearchText] = useState(initialSearchText);
  const [selectedType, setSelectedType] = useState(initialType);
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);

  // Update URL when filters change
  const updateUrlParams = (params) => {
    // Create a new URLSearchParams object
    const newParams = new URLSearchParams(searchParams);
    
    // Update or remove parameters based on values
    Object.entries(params).forEach(([key, value]) => {
      if (value && value !== "") {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    
    // Update the URL without reloading the page
    setSearchParams(newParams);
  };

  // Fetch countries for filter
  useEffect(() => {
    const fetchCountries = async () => {
      setFetchingCountries(true);
      try {
        const response = await api.get("/panel/country");
        setCountries(response.data.data);
      } catch (error) {
        message.error("خطا در دریافت لیست کشورها");
      } finally {
        setFetchingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  // تابع debounce برای جستجو
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // دریافت اطلاعات برندها از API با فیلترها
  const fetchBrands = async (params = {}) => {
    setLoading(true);
    try {
      const { page = 1, per_page = 25, search = "", type = "", country = "" } = params;
      
      const response = await api.get(`/panel/brand`, {
        params: {
          page,
          per_page,
          search: search.trim(),
          type,
          country,
        },
      });
      
      setBrands(response.data.data);
      setPagination({
        current: response.data.meta.current_page,
        pageSize: response.data.meta.per_page,
        total: response.data.meta.total,
      });
    } catch (error) {
      message.error("خطا در دریافت اطلاعات برندها");
    } finally {
      setLoading(false);
    }
  };

  // اعمال debounce روی جستجو
  const debouncedSearch = debounce((value) => {
    const params = {
      page: 1,
      per_page: pagination.pageSize,
      search: value,
      type: selectedType,
      country: selectedCountry,
    };
    
    updateUrlParams({ search: value, page: 1 });
    fetchBrands(params);
  }, 500);

  // تغییر مقدار جستجو
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  // تغییر نوع برند
  const handleTypeChange = (value) => {
    setSelectedType(value);
    updateUrlParams({ type: value, page: 1 });
    
    fetchBrands({
      page: 1,
      per_page: pagination.pageSize,
      search: searchText,
      type: value,
      country: selectedCountry,
    });
  };

  // تغییر کشور
  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    updateUrlParams({ country: value, page: 1 });
    
    fetchBrands({
      page: 1,
      per_page: pagination.pageSize,
      search: searchText,
      type: selectedType,
      country: value,
    });
  };

  // دریافت اولیه داده‌ها با فیلترهای URL
  useEffect(() => {
    fetchBrands({
      page: initialPage,
      per_page: initialPageSize,
      search: initialSearchText,
      type: initialType,
      country: initialCountry,
    });
  }, []);

  // تغییر صفحه‌بندی
  const handleTableChange = (pagination) => {
    updateUrlParams({ 
      page: pagination.current,
      per_page: pagination.pageSize 
    });
    
    fetchBrands({
      page: pagination.current,
      per_page: pagination.pageSize,
      search: searchText,
      type: selectedType,
      country: selectedCountry,
    });
  };

  // حذف برند
  const handleDelete = async (id) => {
    try {
      await api.delete(`/panel/brand/${id}`);
      message.success("برند با موفقیت حذف شد");
      
      fetchBrands({
        page: pagination.current,
        per_page: pagination.pageSize,
        search: searchText,
        type: selectedType,
        country: selectedCountry,
      });
    } catch (error) {
      message.error("خطا در حذف برند");
    }
  };

  // آپلود فایل اکسل
  const handleExcelUpload = async (file, brandId) => {
    setUploadLoading((prev) => ({ ...prev, [brandId]: true }));
    const formData = new FormData();
    formData.append("file", file);
    formData.append("brand_id", brandId);

    try {
      await api.post("/panel/brand/excel/product-import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      message.success("فایل اکسل با موفقیت آپلود شد");
    } catch (error) {
      message.error("خطا در آپلود فایل اکسل");
    } finally {
      setUploadLoading((prev) => ({ ...prev, [brandId]: false }));
    }
  };

  // نمایش مودال تغییر قیمت
  const showModal = (brandId) => {
    setSelectedBrandId(brandId);
    setIsModalVisible(true);
  };

  // بستن مودال تغییر قیمت
  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleModalSubmit = async (values) => {
    try {
      await api.post(`/panel/brand/change-brand-post-price`, {
        brand_id: selectedBrandId,
        product_price_change_type: values.changeType,
        product_price_change_percentage: values.percentage,
      });
      message.success("تغییر قیمت با موفقیت اعمال شد");
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error("خطا در اعمال تغییر قیمت");
    }
  };

  // پاک کردن فیلترها
  const clearFilters = () => {
    setSearchText("");
    setSelectedType("");
    setSelectedCountry("");
    updateUrlParams({ search: "", type: "", country: "", page: 1 });
    
    fetchBrands({
      page: 1,
      per_page: pagination.pageSize,
    });
  };

  const columns = [
    {
      title: "شناسه",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "عنوان",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "کشور",
      dataIndex: "country",
      key: "country",
      render: (_, record) => {
        // Display country names based on the data structure
        if (record.countries && Array.isArray(record.countries)) {
          return record.countries.map(c => c.name).join(', ');
        }
        return record.country || '-';
      }
    },
    {
      title: "نوع",
      dataIndex: "type",
      key: "type",
      render: (type) => (type === "car" ? "خودرو" : "قطعه"),
    },
    {
      title: "تصویر",
      dataIndex: "image",
      key: "image",
      render: (icon) => (
        icon ? (
          <Image 
            src={icon} 
            alt="تصویر برند" 
            width={100} 
            preview={true}
            style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.2))' }}
          />
        ) : (
          <div style={{ width: 100, height: 60, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            بدون تصویر
          </div>
        )
      ),
    },
    {
      title: "عملیات",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/brands/edit/${record.id}`)}
          ></Button>
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => showModal(record.id)}
          >
            تغییر قیمت
          </Button>
          <Upload
            showUploadList={false}
            accept=".xlsx,.xls"
            beforeUpload={(file) => {
              handleExcelUpload(file, record.id);
              return false;
            }}
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
              loading={uploadLoading[record.id]}
            >
              آپلود اکسل
            </Button>
          </Upload>
          <Popconfirm
            title="آیا از حذف این برند اطمینان دارید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله"
            cancelText="خیر"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}></Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div>
        <div className="mb-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">مدیریت برندها</h1>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/brands/add")}
          >
            افزودن برند جدید
          </Button>
        </div>
        
        {/* Filter Section */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Input
                placeholder="جستجوی برندها..."
                prefix={<SearchOutlined />}
                onChange={handleSearchChange}
                value={searchText}
                allowClear
              />
            </Col>
            
            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Select
                placeholder="نوع برند"
                style={{ width: '100%' }}
                onChange={handleTypeChange}
                value={selectedType || undefined}
                allowClear
              >
                <Option value="car">خودرو</Option>
                <Option value="part">قطعه</Option>
              </Select>
            </Col>
            
            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Select
                placeholder="کشور سازنده"
                style={{ width: '100%' }}
                onChange={handleCountryChange}
                value={selectedCountry || undefined}
                loading={fetchingCountries}
                allowClear
              >
                {countries.map(country => (
                  <Option key={country.id} value={country.id.toString()}>
                    {country.name}
                  </Option>
                ))}
              </Select>
            </Col>
            
            <Col xs={24} sm={24} md={4} lg={4} xl={4}>
              <Button 
                onClick={clearFilters} 
                style={{ width: '100%' }}
                icon={<FilterOutlined />}
              >
                پاک کردن فیلترها
              </Button>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={brands}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `مجموع: ${total} برند`,
          }}
          onChange={handleTableChange}
        />

        <Modal
          title="تغییر قیمت محصولات برند"
          visible={isModalVisible}
          onCancel={handleModalCancel}
          footer={null}
        >
          <Form form={form} onFinish={handleModalSubmit} layout="vertical">
            <Form.Item
              name="changeType"
              label="نوع تغییر قیمت محصولات"
              rules={[
                {
                  required: true,
                  message: "لطفاً نوع تغییر قیمت را انتخاب کنید",
                },
              ]}
            >
              <Select placeholder="نوع تغییر قیمت را انتخاب کنید">
                <Option value="increase">افزایش قیمت</Option>
                <Option value="decrease">کاهش قیمت</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="percentage"
              label="درصد تغییر قیمت محصولات"
              rules={[
                {
                  required: true,
                  message: "لطفاً درصد تغییر قیمت را وارد کنید",
                },
                {
                  pattern: /^[0-9]+$/,
                  message: "لطفاً یک عدد معتبر وارد کنید",
                },
              ]}
            >
              <Input placeholder="درصد تغییر قیمت" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                اعمال تغییرات
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Card>
  );
};

export default BrandIndex;